/**
 * Indicador visual do estado da conexão /ws/live.
 *
 * - `open`         → bolinha verde pulsante + "ao vivo"
 * - `connecting`   → bolinha âmbar sem pulso + "conectando"
 * - `reconnecting` → bolinha âmbar pulsante + "reconectando"
 * - `closed`       → bolinha cinza + "offline"
 *
 * Reaproveita as classes `nx-live-badge*` e `nx-live-dot` existentes;
 * quando o estado é âmbar ou cinza, um `style` inline sobrescreve a
 * cor para usar tokens de warning/neutral em vez de success.
 */
import { useLiveConnection, type LiveConnStatus } from '../lib/useLiveEvents.js';

function label(s: LiveConnStatus): string {
  switch (s) {
    case 'open': return 'ao vivo';
    case 'connecting': return 'conectando';
    case 'reconnecting': return 'reconectando';
    case 'closed': return 'offline';
  }
}

function color(s: LiveConnStatus): string {
  switch (s) {
    case 'open': return 'var(--nx-success-500)';
    case 'connecting':
    case 'reconnecting': return 'var(--nx-warning-500)';
    case 'closed': return 'var(--nx-neutral-400)';
  }
}

export function LiveIndicator({ compact = false }: { compact?: boolean }) {
  const { status } = useLiveConnection();
  const on = status === 'open';
  const cls = on ? 'nx-live-badge nx-live-badge--on' : 'nx-live-badge';
  const inlineStyle = on
    ? undefined
    : {
        color: color(status),
        borderColor: 'var(--nx-line-strong)',
        background: 'var(--nx-bg)',
      };
  return (
    <span className={cls} style={inlineStyle} aria-live="polite" title={`Conexão em tempo real: ${label(status)}`}>
      <span
        className="nx-live-dot"
        style={
          !on
            ? { background: color(status), animation: status === 'reconnecting' ? 'nx-ripple 1.4s ease-out infinite' : undefined }
            : undefined
        }
      />
      {compact ? null : label(status)}
    </span>
  );
}
